import { useMemo } from "react";
import type { ProductVariant } from "@lib/product/types";
import type { RawProductAttribute } from "@graphql/generated/types";

import { SizeLocales } from "@lib/constants/filtersConstants";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import {
    getShoeSizeSelectables,
    getNotShoeSizeSelectables,
    isSizeProbablyOneSize,
    isFootwear,
} from "@lib/utils/pdp/pdpShoeSizes";
import { shoeConventionForSite } from "@config/site/site-config";
import { useVariantsAvailability } from "@hooks/useCartItemsAvailability";
import { useLocale } from "next-intl";
import { useProductSizeMicrocopies } from "./useProductSizeMicrocopies";

interface UseProductSizeInfoParams {
    variants: ProductVariant[];
    attributes: RawProductAttribute[];
    globalShoeSizeConvention?: string;
}

export const useProductSizeInfo = ({
    variants,
    attributes,
    globalShoeSizeConvention,
}: UseProductSizeInfoParams) => {
    const locale = useLocale();
    const microcopies = useProductSizeMicrocopies();

    const { gender, targetGroup, materialGroup } = useMemo(() => {
        return {
            gender: getProductAttributeValue(attributes, Attributes.Gender, locale),
            materialGroup: getProductAttributeValue(attributes, Attributes.MaterialGroup, locale),
            targetGroup: getProductAttributeValue(attributes, Attributes.TargetGroup, locale),
        };
    }, [attributes, locale]);

    const { isProbablyOneSize, hasAlternativeSize, isFootwearProduct } = useMemo(() => {
        return {
            isProbablyOneSize: isSizeProbablyOneSize(variants),
            hasAlternativeSize: materialGroup === microcopies.guideGloves,
            isFootwearProduct: isFootwear(attributes, locale),
        };
    }, [variants, attributes, materialGroup, microcopies, locale]);

    const { outOfStockSkus } = useVariantsAvailability(variants);

    const sizeLabels = useMemo(() => {
        if (isProbablyOneSize) return [];

        const outOfStockSizes = new Set(
            variants
                .filter((variant) => outOfStockSkus.includes(variant.sku))
                .map((variant) => getProductAttributeValue(variant.attributesRaw, Attributes.size))
        );
        const labels = isFootwearProduct
            ? getShoeSizeSelectables(variants)
            : getNotShoeSizeSelectables(variants);

        return labels.map((label) =>
            outOfStockSizes.has(label.sizeData) ? { ...label, available: false } : label
        );
    }, [isProbablyOneSize, isFootwearProduct, variants, outOfStockSkus]);

    const firstLabel = sizeLabels.length > 0 ? sizeLabels[0] : null;

    const availableConventions = shoeConventionForSite.filter((sizeLabel) => {
        if (!firstLabel || !sizeLabel) return false;
        return !!firstLabel[`size${sizeLabel}`];
    });

    if (availableConventions.length === 0 && !hasAlternativeSize) {
        availableConventions.push(SizeLocales.EU);
    }

    const shoeSizeSystems = availableConventions.map((sizeLabel) => {
        return {
            key: sizeLabel,
            label: microcopies[`label${sizeLabel}` as keyof typeof microcopies],
            aria: microcopies[`aria${sizeLabel}` as keyof typeof microcopies],
        };
    });

    const currentlyUsedUnit = globalShoeSizeConvention || availableConventions[0];
    const selectedShoeSizeSystem = shoeSizeSystems?.find((s) => s?.key === currentlyUsedUnit) ?? {
        key: SizeLocales.EU,
        label: microcopies.labelEU,
        aria: microcopies.ariaEU,
    };

    return {
        microcopies,
        gender,
        targetGroup,
        materialGroup,
        isProbablyOneSize,
        hasAlternativeSize,
        isFootwearProduct,
        sizeLabels,
        availableConventions,
        shoeSizeSystems,
        currentlyUsedUnit,
        selectedShoeSizeSystem,
    };
};
